import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import PatientMarkersChart from "@/components/PatientMarkersChart";

const EXAM_TYPES = [
  "Ferritina", "B12 (Cobalamina)", "Vitamina D", "Hemograma", "Colesterol (lipidograma)", "TSH", "Glicemia", "PCR", "Outro",
];

export default function ExamsPage() {
  const [exams, setExams] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [examType, setExamType] = useState(EXAM_TYPES[0]);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/exams");
      setExams(data);
    } catch (_) {}
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/exams/upload?exam_type=${encodeURIComponent(examType)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Exame enviado.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-8" data-testid="exams-page">
      <div>
        <div className="overline text-accent mb-3">Exames laboratoriais</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Envio e análise clínica</h1>
        <p className="text-muted-foreground mt-2">Envie seus exames em PDF ou foto. Nosso farmacêutico realizará a análise.</p>
      </div>

      {/* Uploader */}
      <div className="bg-card border border-border rounded-sm p-8" data-testid="exam-uploader">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="overline text-muted-foreground mb-2 block">Tipo de exame</label>
            <select
              value={examType}
              onChange={(e) => setExamType(e.target.value)}
              className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="exam-type-select"
            >
              {EXAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="overline text-muted-foreground mb-2 block">Arquivo (PDF, JPG, PNG · até 10MB)</label>
            <input
              ref={inputRef}
              type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={onFile} disabled={uploading}
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-primary file:text-primary-foreground file:font-semibold hover:file:bg-primary/90 cursor-pointer"
              data-testid="exam-file-input"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Upload className="w-4 h-4" /> {uploading ? "Enviando…" : "Pronto para envio"}
          </div>
        </div>
      </div>

      {/* Exam list */}
      <div>
        <div className="overline text-muted-foreground mb-4">Seus exames ({exams.length})</div>
        {exams.length === 0 ? (
          <div className="bg-card border border-border rounded-sm p-10 text-center text-muted-foreground">
            Você ainda não enviou nenhum exame.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {exams.map((ex) => {
              const isPdf = ex.content_type === "application/pdf";
              return (
                <div key={ex.id} className="bg-card border border-border rounded-sm p-5" data-testid={`exam-item-${ex.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isPdf ? <FileText className="w-5 h-5 text-primary" /> : <ImageIcon className="w-5 h-5 text-primary" />}
                      <span className="font-heading font-semibold text-sm">{ex.exam_type}</span>
                    </div>
                    {ex.status === "pending_review" && (
                      <span className="inline-flex items-center gap-1 text-xs text-warning overline">
                        <Clock className="w-3 h-3" /> Em análise
                      </span>
                    )}
                    {ex.status === "reviewed" && !ex.altered && (
                      <span className="inline-flex items-center gap-1 text-xs text-success overline">
                        <CheckCircle2 className="w-3 h-3" /> Normal
                      </span>
                    )}
                    {ex.altered && (
                      <span className="inline-flex items-center gap-1 text-xs text-destructive overline">
                        <AlertTriangle className="w-3 h-3" /> Alterado
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mb-1 truncate">{ex.original_filename}</div>
                  <div className="text-xs text-muted-foreground">{new Date(ex.created_at).toLocaleDateString("pt-BR")}</div>
                  {ex.observations && <div className="text-xs mt-2 p-2 bg-muted rounded-sm">{ex.observations}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Marker history chart (Etapa Painel do Paciente) */}
      <div>
        <div className="overline text-muted-foreground mb-4">Seus marcadores clínicos</div>
        <PatientMarkersChart />
      </div>
    </div>
  );
}
